import { useHydrated } from '@tanstack/react-router';
import {
    useQuery,
    type DefinedUseQueryResult,
    type QueryClient,
    type UseQueryOptions,
    type UseQueryResult,
    DefinedInitialDataOptions,
    UndefinedInitialDataOptions,
} from '@tanstack/react-query';
import type { DefaultError, Query, QueryKey } from '@tanstack/query-core';

function resolveHydratedEnabled<
    TQueryFnData,
    TError,
    TData,
    TQueryKey extends QueryKey,
>(
    hydrated: boolean,
    enabled:
        | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>['enabled']
        | undefined,
) {
    if (typeof enabled === 'function') {
        return (query: Query<TQueryFnData, TError, TQueryFnData, TQueryKey>) =>
            hydrated && enabled(query);
    }

    return hydrated && (enabled ?? true);
}

export function useHydratedQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError>;

export function useHydratedQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    options: UndefinedInitialDataOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey
    >,
    queryClient?: QueryClient,
): UseQueryResult<TData, TError>;

export function useHydratedQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient,
): UseQueryResult<TData, TError>;

export function useHydratedQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> {
    const hydrated = useHydrated();

    return useQuery(
        {
            ...options,
            enabled: resolveHydratedEnabled(hydrated, options.enabled),
        },
        queryClient,
    );
}
